// Wire types for the Niu WebSocket protocol.
//
// IMPORTANT: the wire deliberately mixes snake_case (`tool_name`, `is_error`,
// `since_seq`) with intentional camelCase (`waitingForTool`, `questionId`,
// `messageId`). The decoder MUST decode by exact wire key — no global
// camelCase strategy. The encoders/decoders in `./codec.ts` are field-by-field.

export type NiuMessageType =
  | 'user_message'
  | 'assistant_message'
  | 'tool_call'
  | 'tool_result'
  | 'thinking'
  | 'status'
  | 'error'
  | 'reaction'
  | 'agents_available'
  | 'device_register'
  | 'device_info'
  | 'device_unregister'
  | 'devices_list_request'
  | 'device_delete_request'
  | 'device_rename_request'
  | 'devices_list'
  | 'question'
  | 'answer'
  | 'presence'
  | 'catchup'
  | 'catchup_complete'
  | 'assistant_message_delta'
  | 'thinking_delta'
  | 'tool_call_delta'
  | 'message_batch'
  | 'fetch_earlier_messages'
  | 'earlier_complete'
  | 'inline_snippet'
  | 'inline_snippet_reload'
  | 'inline_snippet_closed'
  | 'rate_limit';

export interface Attachment {
  id: string;
  name: string;
  mimeType: string;
  size: number;
  /** Server-relative or absolute download URL; nil on upload-response stubs. */
  url?: string | null;
  /** PDFs: page count, host-rendered (poppler). Absent otherwise. */
  pageCount?: number;
  /** PDFs: download URL of the host-rendered first-page preview image. */
  previewUrl?: string | null;
}

export interface AgentDescriptor {
  id: string;
  name: string;
}

// ---------- Outbound payloads ----------

export interface UserMessagePayload {
  text: string;
  attachments?: Attachment[];
  /** Selector id of the wired agent this message targets. */
  agent?: string;
}

export interface AnswerPayload {
  questionId: string;
  selectedOption: string;
}

export interface PresencePayload {
  state: 'active' | 'inactive';
}

export interface CatchupPayload {
  /** Wire: `since_seq`. */
  sinceSeq: number;
  limit?: number;
}

export interface DeviceRegisterPayload {
  token: string;
  platform: 'ios' | 'macos' | 'android' | 'web';
  environment: 'sandbox' | 'production';
  /** Wire: `bundle_id`. */
  bundleId: string;
  /** Wire: `app_version`. */
  appVersion?: string;
  locale?: string;
  /** Wire: `device_id`. */
  deviceId?: string;
}

export interface DeviceUnregisterPayload {
  token: string;
  environment: 'sandbox' | 'production';
}

/** APNs-independent device metadata (protocol §3.10). Fired on every WS
 *  connect so the server's `niu_known_devices` row carries platform /
 *  version / system name even without a push token. */
export interface DeviceInfoPayload {
  /** Wire: `device_id`. */
  deviceId?: string;
  platform: 'ios' | 'macos' | 'android' | 'web';
  /** Wire: `bundle_id`. */
  bundleId?: string;
  /** Wire: `app_version`. */
  appVersion?: string;
  locale?: string;
  /** Wire: `device_name`. OS-supplied label (`navigator.userAgent`-derived
   *  here); preferred over the token-side `device_name` in `devices_list`
   *  but overridden by a manual rename. */
  deviceName?: string;
}

/** Client → server: ask for the device roster. Empty payload. */
export interface DevicesListRequestPayload {}

/** Client → server: forget a device by id (protocol §3.8). */
export interface DeviceDeleteRequestPayload {
  /** Wire: `device_id`. */
  deviceId: string;
}

/** Client → server: set the manual rename override (protocol §3.9).
 *  Empty / whitespace `deviceName` clears the override. */
export interface DeviceRenameRequestPayload {
  /** Wire: `device_id`. */
  deviceId: string;
  /** Wire: `device_name`. */
  deviceName: string;
}

/** Server frame: snapshot of every device known to the bucket (§4.12). */
export interface DeviceInfo {
  /** Wire: `device_id`. */
  deviceId: string;
  /** Wire: `device_name`. */
  deviceName: string | null;
  platform: 'ios' | 'macos' | 'android' | 'web' | null;
  /** Wire: `bundle_id`. */
  bundleId: string | null;
  /** Wire: `app_version`. */
  appVersion: string | null;
  locale: string | null;
  /** Wire: `first_seen`, unix seconds. */
  firstSeen: number;
  /** Wire: `last_seen`, unix seconds. */
  lastSeen: number;
  connected: boolean;
}

export interface DevicesListPayload {
  devices: DeviceInfo[];
}

// ---------- Inbound payloads ----------

export interface AssistantMessagePayload {
  text: string;
  agent?: AgentDescriptor;
  attachments?: Attachment[];
}

export interface ToolCallPayload {
  /** Wire: `tool_name`. */
  toolName: string;
  /** Opaque JSON string, never parsed by this layer. */
  input: string;
  /** Wire: `tool_use_id`. */
  toolUseId: string;
  agent?: AgentDescriptor;
}

export interface ToolResultPayload {
  /** Wire: `tool_use_id`. */
  toolUseId: string;
  output: string;
  /** Wire: `is_error`. */
  isError: boolean;
  agent?: AgentDescriptor;
}

export interface ThinkingPayload {
  text: string;
  agent?: AgentDescriptor;
}

export type StatusState =
  | 'idle'
  | 'thinking'
  | 'responding'
  /** Wire is camelCase `waitingForTool`, not snake_case. */
  | 'waitingForTool'
  | 'compacting'
  | string; // unknown forward-compatible

export interface StatusPayload {
  state: StatusState;
  detail?: string;
  agent?: AgentDescriptor;
}

/** One rolling usage window. `resetsAt` is a unix-seconds timestamp. */
export interface RateLimitWindow {
  usedPercent: number;
  windowMinutes: number;
  resetsAt: number;
}

/** Provider usage snapshot (today codex: 5h primary + weekly secondary).
 *  `updatedAt` is unix ms — the sidebar hides the bars once it's older
 *  than an hour so a stale snapshot can't linger after the agent goes quiet. */
export interface RateLimitPayload {
  primary: RateLimitWindow | null;
  secondary: RateLimitWindow | null;
  updatedAt: number;
  agent?: AgentDescriptor;
}

export interface ErrorPayload {
  message?: string;
  code?: string;
  /** §1.1 / §1.2 retention-gap codes carry the bucket's surviving min. */
  firstKnownSeq?: number;
  requestedSince?: number;
  requestedBefore?: number;
}

export interface ReactionPayload {
  /** Wire camelCase: `messageId`. Primary match id — usually the server-minted
   *  `niu-…` echo id. */
  messageId: string;
  /** Additional ids naming the same logical message (e.g. the originator's
   *  pre-echo local id). Client should match the reaction against any id in
   *  `[messageId, ...alsoMatch]`. */
  alsoMatch?: string[];
  /** `null` or empty string ⇒ reaction removed (protocol §4.6). */
  emoji?: string | null;
  agent?: AgentDescriptor;
}

export interface QuestionOption {
  label: string;
  selectedLabel?: string;
  value?: string;
}

export interface QuestionPayload {
  /** Wire camelCase: `questionId`. */
  questionId: string;
  title: string;
  question: string;
  options: QuestionOption[];
  agent?: AgentDescriptor;
}

export interface AgentsAvailablePayload {
  agents: AgentDescriptor[];
}

export interface CatchupCompletePayload {
  /** Wire: `latest_seq`. */
  latestSeq: number;
  more: boolean;
}

/** §4.13 wrapper — multiple replay envelopes in one WS frame. */
export interface MessageBatchPayload {
  envelopes: NiuMessage[];
}

/** §3.11 client→server: backward pagination. */
export interface FetchEarlierMessagesPayload {
  /** Wire: `before_seq`. */
  beforeSeq: number;
  limit?: number;
}

/** §4.14 server→client terminator for `fetch_earlier_messages`. */
export interface EarlierCompletePayload {
  /** Wire: `earliest_seq`. */
  earliestSeq: number;
  more: boolean;
}

// Streaming deltas (opt-in via ?stream=text,thinking,tool_call).
// All keys camelCase on the wire EXCEPT tool_use_id in tool_call_delta.
export interface AssistantMessageDeltaPayload {
  messageId: string;
  blockIndex: number;
  delta: string;
  agent?: AgentDescriptor;
}

export interface ThinkingDeltaPayload {
  messageId: string;
  blockIndex: number;
  delta: string;
  agent?: AgentDescriptor;
}

export interface ToolCallDeltaPayload {
  messageId: string;
  blockIndex: number;
  /** Wire: `tool_use_id`. */
  toolUseId: string;
  inputDelta: string;
  agent?: AgentDescriptor;
}

// Inline snippets — ephemeral live web views the agent shows in a split pane.
// All keys camelCase on the wire (these are new frames — no legacy snake_case
// to honour). The view dies with the agent's container.
export interface InlineSnippetPayload {
  /** Same-origin proxy URL, e.g. `/_inline/<token>/`. */
  url: string;
  title: string;
  /** Identifies this open instance — reload/closed frames target it. */
  token: string;
  agent?: AgentDescriptor;
}

export interface InlineSnippetReloadPayload {
  token: string;
  agent?: AgentDescriptor;
}

export interface InlineSnippetClosedPayload {
  token: string;
  agent?: AgentDescriptor;
}

// ---------- Discriminated union ----------

export type NiuPayload =
  | { kind: 'user_message'; data: UserMessagePayload }
  | { kind: 'assistant_message'; data: AssistantMessagePayload }
  | { kind: 'tool_call'; data: ToolCallPayload }
  | { kind: 'tool_result'; data: ToolResultPayload }
  | { kind: 'thinking'; data: ThinkingPayload }
  | { kind: 'status'; data: StatusPayload }
  | { kind: 'error'; data: ErrorPayload }
  | { kind: 'reaction'; data: ReactionPayload }
  | { kind: 'question'; data: QuestionPayload }
  | { kind: 'answer'; data: AnswerPayload }
  | { kind: 'presence'; data: PresencePayload }
  | { kind: 'catchup'; data: CatchupPayload }
  | { kind: 'catchup_complete'; data: CatchupCompletePayload }
  | { kind: 'agents_available'; data: AgentsAvailablePayload }
  | { kind: 'device_register'; data: DeviceRegisterPayload }
  | { kind: 'device_info'; data: DeviceInfoPayload }
  | { kind: 'device_unregister'; data: DeviceUnregisterPayload }
  | { kind: 'devices_list_request'; data: DevicesListRequestPayload }
  | { kind: 'device_delete_request'; data: DeviceDeleteRequestPayload }
  | { kind: 'device_rename_request'; data: DeviceRenameRequestPayload }
  | { kind: 'devices_list'; data: DevicesListPayload }
  | { kind: 'assistant_message_delta'; data: AssistantMessageDeltaPayload }
  | { kind: 'thinking_delta'; data: ThinkingDeltaPayload }
  | { kind: 'tool_call_delta'; data: ToolCallDeltaPayload }
  | { kind: 'message_batch'; data: MessageBatchPayload }
  | { kind: 'fetch_earlier_messages'; data: FetchEarlierMessagesPayload }
  | { kind: 'earlier_complete'; data: EarlierCompletePayload }
  | { kind: 'inline_snippet'; data: InlineSnippetPayload }
  | { kind: 'inline_snippet_reload'; data: InlineSnippetReloadPayload }
  | { kind: 'inline_snippet_closed'; data: InlineSnippetClosedPayload }
  | { kind: 'rate_limit'; data: RateLimitPayload };

export interface NiuMessage {
  id: string;
  type: NiuMessageType;
  /** Server-stamped per-bucket dense seq on durable-eligible inbound frames. */
  seq?: number;
  payload: NiuPayload;
}

/** Convenience: project the agent id off any payload that carries one. */
export function payloadAgentId(payload: NiuPayload): string | undefined {
  switch (payload.kind) {
    case 'user_message':
      return payload.data.agent;
    case 'assistant_message':
    case 'tool_call':
    case 'tool_result':
    case 'thinking':
    case 'status':
    case 'reaction':
    case 'question':
    case 'assistant_message_delta':
    case 'thinking_delta':
    case 'tool_call_delta':
    case 'inline_snippet':
    case 'inline_snippet_reload':
    case 'inline_snippet_closed':
    case 'rate_limit':
      return payload.data.agent?.id;
    default:
      return undefined;
  }
}
